import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash, Check, X, RefreshCw, Edit2, Trash2, Save, ExternalLink } from "lucide-react";
import { CONFIG } from "../../config";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/axios";

export default function AdminQuizManager() {
    const { token } = useAuth();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form State
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        text: "",
        type: "MULTIPLE_CHOICE", // MULTIPLE_CHOICE, TEXT, SCALE
        options: [""],
        category: "SPIRITUAL",
        points: 10,
        order: 0
    });

    useEffect(() => {
        fetchQuestions();
    }, []);

    const fetchQuestions = async () => {
        try {
            const res = await api.get("/admin/quiz");
            const data = res.data;
            setQuestions(data);
            // Auto-set order for new questions
            setFormData(prev => ({ ...prev, order: data.length + 1 }));
        } catch (err) {
            toast.error("Failed to load questions");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({
            text: "",
            type: "MULTIPLE_CHOICE",
            options: [""],
            category: "SPIRITUAL",
            points: 10,
            order: questions.length + 1
        });
    };

    const handleSave = async () => {
        if (!formData.text) return toast.error("Question text is required");

        try {
            const path = editingId ? `/admin/quiz/${editingId}` : "/admin/quiz";
            const method = editingId ? "put" : "post";

            await api[method](path, formData);

            toast.success(editingId ? "Question Updated" : "Question Added");
            fetchQuestions();
            resetForm();
        } catch (err) {
            toast.error("Failed to save question");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            await api.delete(`/admin/quiz/${id}`);
            toast.success("Question Deleted");
            fetchQuestions();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    const handleEdit = (q) => {
        setEditingId(q.id);
        setFormData({
            text: q.text,
            type: q.type,
            options: q.options || [],
            category: q.category,
            points: q.points || 10,
            order: q.order
        });
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Option Management
    const addOption = () => setFormData({ ...formData, options: [...formData.options, ""] });
    const updateOption = (idx, val) => {
        const newOpts = [...formData.options];
        newOpts[idx] = val;
        setFormData({ ...formData, options: newOpts });
    };
    const removeOption = (idx) => {
        const newOpts = formData.options.filter((_, i) => i !== idx);
        setFormData({ ...formData, options: newOpts });
    };

    return (
        <div className="p-6 max-w-4xl mx-auto font-body">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-display font-medium text-gray-900 dark:text-gray-100">
                        Gatekeeper Params
                    </h1>
                    <p className="text-gray-500">Manage the vetting questions.</p>
                </div>
                <div className="text-sm text-gray-400">
                    {questions.length} Active Questions
                </div>
            </div>

            {/* Editor Card */}
            <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm mb-8">
                <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                    {editingId ? <Edit2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    {editingId ? "Edit Question" : "Add New Question"}
                </h2>

                <div className="space-y-4">
                    {/* Row 1 */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Question Text</label>
                            <input
                                type="text"
                                value={formData.text}
                                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                                className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent"
                                placeholder="e.g. How do you handle conflict?"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Type</label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent"
                            >
                                <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                                <option value="TEXT">Text Input</option>
                                <option value="SCALE">Scale (1-5)</option>
                            </select>
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Category</label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent"
                            >
                                <option value="SPIRITUAL">Spiritual Alignment</option>
                                <option value="PSYCHO">Psychological</option>
                                <option value="ETHICS">Ethics & Safety</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Points</label>
                            <input
                                type="number"
                                value={formData.points}
                                onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                                className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Order</label>
                            <input
                                type="number"
                                value={formData.order}
                                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent"
                            />
                        </div>
                    </div>

                    {/* Options (Conditional) */}
                    {(formData.type === "MULTIPLE_CHOICE" || formData.type === "SCALE") && (
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase">
                                    {formData.type === "SCALE" ? "Scale Labels (1 to 5)" : "Options"}
                                </label>
                                {formData.type === "SCALE" && (
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, options: ["1", "2", "3", "4", "5"] })}
                                        className="text-[10px] bg-brand-blue/10 text-brand-blue px-2 py-0.5 rounded hover:bg-brand-blue/20 transition-colors flex items-center gap-1"
                                    >
                                        <RefreshCw className="w-3 h-3" /> Auto-set 1-5
                                    </button>
                                )}
                            </div>
                            {formData.options.map((opt, idx) => (
                                <div key={idx} className="flex items-center gap-2 mb-2">
                                    <span className="text-xs text-gray-400 font-mono">{idx + 1}.</span>
                                    <input
                                        type="text"
                                        value={opt}
                                        onChange={(e) => updateOption(idx, e.target.value)}
                                        className="flex-1 p-2 text-sm rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                                    />
                                    <button onClick={() => removeOption(idx)} className="text-red-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                                </div>
                            ))}
                            <button onClick={addOption} className="text-xs font-medium text-brand-blue hover:underline">+ Add Option</button>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={handleSave}
                            className="flex items-center gap-2 px-6 py-2 bg-brand-blue text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            <Save className="w-4 h-4" />
                            {editingId ? "Update Question" : "Save Question"}
                        </button>
                        {editingId && (
                            <button
                                onClick={resetForm}
                                className="px-6 py-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="space-y-3">
                {questions.map((q) => (
                    <div key={q.id} className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-gray-100 dark:border-gray-800 flex items-center gap-4 group hover:shadow-md transition-all">
                        <div className="text-gray-300 font-mono text-sm w-6 text-center">{q.order}</div>

                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${q.category === 'SPIRITUAL' ? 'bg-purple-100 text-purple-600' :
                                        q.category === 'ETHICS' ? 'bg-red-100 text-red-600' :
                                            'bg-blue-100 text-blue-600'
                                    }`}>
                                    {q.category}
                                </span>
                                <span className="text-[10px] text-gray-400 uppercase">{q.type}</span>
                            </div>
                            <h3 className="font-medium text-gray-800 dark:text-gray-200">{q.text}</h3>
                            {q.type === 'MULTIPLE_CHOICE' && (
                                <p className="text-xs text-gray-500 mt-1 truncate max-w-md">
                                    Options: {q.options?.join(", ")}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleEdit(q)} className="p-2 text-gray-400 hover:text-brand-blue">
                                <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(q.id)} className="p-2 text-gray-400 hover:text-red-500">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

        </div>
    );
}
